import React, { useContext } from 'react';
import { Helmet } from 'react-helmet';
import { LanguageContext } from '../contexts/LanguageContext';
import { ThemeContext } from '../contexts/ThemeContext';

const MonitoringObservability = () => {
  const { translations } = useContext(LanguageContext);
  const { theme } = useContext(ThemeContext);
  
  return (
    <div className="bg-white dark:bg-gray-900 min-h-screen">
      <Helmet>
        <title>{translations['observability.pageTitle'] || 'Monitoring and Observability | Kubernetes | Fepsec'}</title>
        <meta name="description" content="Comprehensive monitoring and observability solutions for Kubernetes and OpenShift environments using Prometheus, Grafana, ELK Stack, and OpenTelemetry" />
      </Helmet>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-800 to-blue-900 text-white py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6" data-aos="fade-up">{translations['observability.heroTitle'] || 'Monitoring and Observability'}</h1>
            <p className="text-xl text-blue-100 mb-8" data-aos="fade-up" data-aos-delay="100">
              {translations['observability.heroSubtitle'] || 'Comprehensive visibility and insights for your Kubernetes and OpenShift environments'}
            </p>
          </div>
        </div>
      </div>
      
      {/* Introduction Section */}
      <div className="container mx-auto px-6 py-16">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-8 mb-10" data-aos="fade-up">
            <h2 className="text-3xl font-bold text-gray-800 dark:text-blue-100 mb-6">{translations['observability.mainTitle'] || 'The Foundation of Reliable Cloud-Native Infrastructure'}</h2>
            <p className="text-gray-700 dark:text-gray-300 text-lg mb-6">
              {translations['observability.mainDescription1'] || 'In modern Kubernetes and OpenShift environments, comprehensive observability is not just a feature—it\'s a critical operational necessity. Our Enterprise Observability solutions provide deep insights across your entire container ecosystem, enabling you to detect, diagnose, and resolve issues before they impact your business.'}
            </p>
            <p className="text-gray-700 dark:text-gray-300 text-lg mb-6">
              {translations['observability.mainDescription2'] || 'Fepsec\'s observability expertise combines the three essential pillars of modern observability: metrics, logs, and traces. By integrating best-in-class open-source tools, we create a unified observability platform tailored to your specific operational requirements and business objectives.'}
            </p>
          </div>
          
          {/* Three Pillars of Observability */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden" data-aos="fade-up" data-aos-delay="100">
              <div className="bg-blue-600 p-4 text-white">
                <h3 className="text-xl font-bold">{translations['observability.metrics.title'] || 'Metrics'}</h3>
              </div>
              <div className="p-6">
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  {translations['observability.metrics.description'] || 'Quantitative measurements of system behavior over time, providing real-time performance insights and trend analysis.'}
                </p>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2">
                  <li>{translations['observability.metrics.point1'] || 'Resource utilization'}</li>
                  <li>{translations['observability.metrics.point2'] || 'Application performance'}</li>
                  <li>{translations['observability.metrics.point3'] || 'Business KPIs'}</li>
                  <li>{translations['observability.metrics.point4'] || 'SLO/SLI measurement'}</li>
                </ul>
              </div>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden" data-aos="fade-up" data-aos-delay="200">
              <div className="bg-blue-600 p-4 text-white">
                <h3 className="text-xl font-bold">{translations['observability.logs.title'] || 'Logs'}</h3>
              </div>
              <div className="p-6">
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  {translations['observability.logs.description'] || 'Detailed event records that provide context around system and application behavior for troubleshooting and audit purposes.'}
                </p>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2">
                  <li>{translations['observability.logs.point1'] || 'Application logs'}</li>
                  <li>{translations['observability.logs.point2'] || 'System events'}</li>
                  <li>{translations['observability.logs.point3'] || 'Error messages'}</li>
                  <li>{translations['observability.logs.point4'] || 'Audit trails'}</li>
                </ul>
              </div>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden" data-aos="fade-up" data-aos-delay="300">
              <div className="bg-blue-600 p-4 text-white">
                <h3 className="text-xl font-bold">{translations['observability.traces.title'] || 'Traces'}</h3>
              </div>
              <div className="p-6">
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  {translations['observability.traces.description'] || 'End-to-end visibility of request flows across distributed services, helping identify bottlenecks and dependencies.'}
                </p>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2">
                  <li>{translations['observability.traces.point1'] || 'Request paths'}</li>
                  <li>{translations['observability.traces.point2'] || 'Service dependencies'}</li>
                  <li>{translations['observability.traces.point3'] || 'Performance bottlenecks'}</li>
                  <li>{translations['observability.traces.point4'] || 'Error propagation'}</li>
                </ul>
              </div>
            </div>
          </div>
          
          {/* Cloud-Native Monitoring Tools */}
          <div className="mb-16" data-aos="fade-up">
            <h2 className="text-3xl font-bold text-gray-800 dark:text-white mb-8 text-center">{translations['observability.tools.title'] || 'Best-in-Class Open Source Observability Tools'}</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-10">
              {/* Prometheus & Grafana */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden">
                <div className="bg-gradient-to-r from-blue-700 to-blue-800 p-6 text-white">
                  <h3 className="text-2xl font-bold mb-2">{translations['observability.prometheus.title'] || 'Prometheus & Grafana'}</h3>
                  <p className="text-blue-100">{translations['observability.prometheus.subtitle'] || 'Industry-standard metrics collection and visualization'}</p>
                </div>
                <div className="p-6">
                  <p className="text-gray-700 dark:text-gray-300 mb-4">
                    {translations['observability.prometheus.description'] || 'Prometheus provides powerful metrics collection with a flexible query language (PromQL), while Grafana delivers customizable dashboards and visualizations. Together, they form the backbone of Kubernetes monitoring.'}
                  </p>
                  <h4 className="font-bold text-gray-800 dark:text-blue-100 mb-2">{translations['observability.prometheus.services'] || 'Our Implementation Services:'}</h4>
                  <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1 mb-4">
                    <li>{translations['observability.prometheus.service1'] || 'Custom metric collection with Prometheus Operator'}</li>
                    <li>{translations['observability.prometheus.service2'] || 'Advanced alerting with Alertmanager'}</li>
                    <li>{translations['observability.prometheus.service3'] || 'Role-based dashboard access in Grafana'}</li>
                    <li>{translations['observability.prometheus.service4'] || 'Automated metric scraping configuration'}</li>
                    <li>{translations['observability.prometheus.service5'] || 'High availability and long-term storage solutions'}</li>
                  </ul>
                </div>
              </div>
              
              {/* ELK Stack */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden">
                <div className="bg-gradient-to-r from-blue-700 to-blue-800 p-6 text-white">
                  <h3 className="text-2xl font-bold mb-2">{translations['observability.elk.title'] || 'ELK Stack & Loki'}</h3>
                  <h4 className="text-blue-100">{translations['observability.elk.subtitle'] || 'Comprehensive log aggregation and analysis'}</h4>
                </div>
                <div className="p-6">
                  <p className="text-gray-700 dark:text-gray-300 mb-4">
                    {translations['observability.elk.description'] || 'The ELK Stack (Elasticsearch, Logstash, Kibana) and Grafana Loki provide powerful log collection, indexing, and visualization capabilities, enabling you to search and analyze logs at scale.'}
                  </p>
                  <h4 className="font-bold text-gray-800 dark:text-blue-100 mb-2">{translations['observability.elk.services'] || 'Our Implementation Services:'}</h4>
                  <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1 mb-4">
                    <li>{translations['observability.elk.service1'] || 'Centralized log collection and indexing'}</li>
                    <li>{translations['observability.elk.service2'] || 'Custom log parsing and enrichment'}</li>
                    <li>{translations['observability.elk.service3'] || 'Log retention policies and lifecycle management'}</li>
                    <li>{translations['observability.elk.service4'] || 'Integration with existing SIEM systems'}</li>
                    <li>{translations['observability.elk.service5'] || 'Log-based alerting and anomaly detection'}</li>
                  </ul>
                </div>
              </div>
              
              {/* OpenTelemetry */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden">
                <div className="bg-gradient-to-r from-blue-700 to-blue-800 p-6 text-white">
                  <h3 className="text-2xl font-bold mb-2">{translations['observability.opentelemetry.title'] || 'OpenTelemetry'}</h3>
                  <h4 className="text-blue-100">{translations['observability.opentelemetry.subtitle'] || 'Unified observability data collection'}</h4>
                </div>
                <div className="p-6">
                  <p className="text-gray-700 dark:text-gray-300 mb-4">
                    {translations['observability.opentelemetry.description'] || 'OpenTelemetry provides a standardized way to collect and export telemetry data (metrics, logs, and traces) from your applications and infrastructure, eliminating vendor lock-in and enabling a consistent observability strategy.'}
                  </p>
                  <h4 className="font-bold text-gray-800 dark:text-blue-100 mb-2">{translations['observability.opentelemetry.services'] || 'Our Implementation Services:'}</h4>
                  <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1 mb-4">
                    <li>{translations['observability.opentelemetry.service1'] || 'Instrumentation of applications with OpenTelemetry SDK'}</li>
                    <li>{translations['observability.opentelemetry.service2'] || 'Implementation of OpenTelemetry Collector'}</li>
                    <li>{translations['observability.opentelemetry.service3'] || 'Integration with existing observability backends'}</li>
                    <li>{translations['observability.opentelemetry.service4'] || 'Custom instrumentation for business-critical systems'}</li>
                    <li>{translations['observability.opentelemetry.service5'] || 'Distributed tracing implementation with Jaeger'}</li>
                  </ul>
                </div>
              </div>
              
              {/* Service Mesh Observability */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden">
                <div className="bg-gradient-to-r from-blue-700 to-blue-800 p-6 text-white">
                  <h3 className="text-2xl font-bold mb-2">{translations['observability.servicemesh.title'] || 'Service Mesh Observability'}</h3>
                  <h4 className="text-blue-100">{translations['observability.servicemesh.subtitle'] || 'Network-level insights for microservices'}</h4>
                </div>
                <div className="p-6">
                  <p className="text-gray-700 dark:text-gray-300 mb-4">
                    {translations['observability.servicemesh.description'] || 'Service meshes like Istio and Linkerd provide deep network-level observability for microservices communication, enabling detailed traffic monitoring, distributed tracing, and service performance analytics.'}
                  </p>
                  <h4 className="font-bold text-gray-800 dark:text-blue-100 mb-2">{translations['observability.servicemesh.services'] || 'Our Implementation Services:'}</h4>
                  <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1 mb-4">
                    <li>{translations['observability.servicemesh.service1'] || 'Service mesh deployment and configuration'}</li>
                    <li>{translations['observability.servicemesh.service2'] || 'Traffic flow visualization and analysis'}</li>
                    <li>{translations['observability.servicemesh.service3'] || 'Golden signal monitoring (latency, traffic, errors, saturation)'}</li>
                    <li>{translations['observability.servicemesh.service4'] || 'Service-level objective (SLO) definition and tracking'}</li>
                    <li>{translations['observability.servicemesh.service5'] || 'Advanced service mesh observability dashboards'}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          
          {/* Benefits Section */}
          <div className="bg-blue-50 dark:bg-gray-800 rounded-xl p-8 mb-16" data-aos="fade-up">
            <h2 className="text-3xl font-bold text-gray-800 dark:text-blue-100 mb-6 text-center">{translations['observability.benefits.title'] || 'Benefits of Our Observability Solutions'}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold text-blue-700 dark:text-blue-400 mb-3">{translations['observability.benefits.mttr.title'] || 'Reduced MTTR'}</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  {translations['observability.benefits.mttr.description'] || 'Decrease mean time to resolution through faster issue detection, diagnosis, and remediation.'}
                </p>
              </div>
              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold text-blue-700 dark:text-blue-400 mb-3">{translations['observability.benefits.proactive.title'] || 'Proactive Operations'}</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  {translations['observability.benefits.proactive.description'] || 'Identify and address potential issues before they impact your users or business.'}
                </p>
              </div>
              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold text-blue-700 dark:text-blue-400 mb-3">{translations['observability.benefits.cost.title'] || 'Cost Optimization'}</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  {translations['observability.benefits.cost.description'] || 'Gain insights into resource utilization to optimize infrastructure costs and efficiency.'}
                </p>
              </div>
              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold text-blue-700 dark:text-blue-400 mb-3">{translations['observability.benefits.collaboration.title'] || 'Enhanced Collaboration'}</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  {translations['observability.benefits.collaboration.description'] || 'Foster DevOps culture with shared visibility and understanding of system behaviors.'}
                </p>
              </div>
              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold text-blue-700 dark:text-blue-400 mb-3">{translations['observability.benefits.improvement.title'] || 'Continuous Improvement'}</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  {translations['observability.benefits.improvement.description'] || 'Data-driven insights to continuously improve application performance and reliability.'}
                </p>
              </div>
              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold text-blue-700 dark:text-blue-400 mb-3">{translations['observability.benefits.scalable.title'] || 'Scalable Solutions'}</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  {translations['observability.benefits.scalable.description'] || 'Observability architectures that grow with your environment, from single cluster to multi-cluster.'}
                </p>
              </div>
            </div>
          </div>
          
          {/* Our Approach */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 mb-16 relative" data-aos="fade-up">
            <h2 className="text-3xl font-bold text-gray-800 dark:text-blue-100 mb-6">{translations['observability.approach.title'] || 'Our Implementation Approach'}</h2>
            <div className="space-y-6">
              <div className="flex items-start">
                <div className="bg-blue-100 text-blue-800 font-bold rounded-full w-8 h-8 flex items-center justify-center mt-1 flex-shrink-0">
                  1
                </div>
                <div className="ml-4">
                  <h3 className="text-xl font-bold text-gray-800 dark:text-blue-100 mb-2">{translations['observability.approach.step1.title'] || 'Assessment & Planning'}</h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    {translations['observability.approach.step1.description'] || 'We begin by assessing your current infrastructure, applications, and monitoring needs to develop a comprehensive observability strategy tailored to your specific requirements.'}
                  </p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="bg-blue-100 text-blue-800 font-bold rounded-full w-8 h-8 flex items-center justify-center mt-1 flex-shrink-0">
                  2
                </div>
                <div className="ml-4">
                  <h3 className="text-xl font-bold text-gray-800 dark:text-blue-100 mb-2">{translations['observability.approach.step2.title'] || 'Infrastructure Deployment'}</h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    {translations['observability.approach.step2.description'] || 'We deploy and configure the core observability components using infrastructure-as-code and GitOps principles, ensuring reproducibility, version control, and consistent environments.'}
                  </p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="bg-blue-100 text-blue-800 font-bold rounded-full w-8 h-8 flex items-center justify-center mt-1 flex-shrink-0">
                  3
                </div>
                <div className="ml-4">
                  <h3 className="text-xl font-bold text-gray-800 dark:text-blue-100 mb-2">{translations['observability.approach.step3.title'] || 'Application Instrumentation'}</h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    {translations['observability.approach.step3.description'] || 'We work with your development teams to implement proper instrumentation across your applications, ensuring meaningful metrics, logs, and traces are collected.'}
                  </p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="bg-blue-100 text-blue-800 font-bold rounded-full w-8 h-8 flex items-center justify-center mt-1 flex-shrink-0">
                  4
                </div>
                <div className="ml-4">
                  <h3 className="text-xl font-bold text-gray-800 dark:text-blue-100 mb-2">{translations['observability.approach.step4.title'] || 'Dashboard & Alert Configuration'}</h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    {translations['observability.approach.step4.description'] || 'We create custom dashboards and alerting rules based on your SLOs and operational requirements, ensuring proactive monitoring of critical systems.'}
                  </p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="bg-blue-100 text-blue-800 font-bold rounded-full w-8 h-8 flex items-center justify-center mt-1 flex-shrink-0">
                  5
                </div>
                <div className="ml-4">
                  <h3 className="text-xl font-bold text-gray-800 dark:text-blue-100 mb-2">{translations['observability.approach.step5.title'] || 'Knowledge Transfer & Support'}</h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    {translations['observability.approach.step5.description'] || 'We provide comprehensive documentation, training, and ongoing support to ensure your team can effectively utilize the observability platform.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Call to Action */}
          <div className="text-center mb-16" data-aos="fade-up">
            <h2 className="text-3xl font-bold text-gray-800 dark:text-blue-100 mb-6">{translations['observability.cta.title'] || 'Ready to Enhance Your Kubernetes Observability?'}</h2>
            <p className="text-gray-700 text-lg mb-8">
              {translations['observability.cta.description'] || 'Contact our team today to discuss how our Enterprise Observability solutions can help you gain deeper insights into your Kubernetes and OpenShift environments.'}
            </p>
            <a 
              href="/contact-us" 
              className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-lg transition duration-300 transform hover:scale-105"
            >
              {translations['observability.cta.button'] || 'Contact Us'}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MonitoringObservability;
